import { z } from "zod";
import {
  DEFAULT_ADMIN_SESSION_MAX_HOURS,
  DEFAULT_MFA_REMEMBER_DAYS,
  MAX_ADMIN_SESSION_MAX_HOURS,
  MAX_MFA_REMEMBER_DAYS,
  mfaModeSchema,
} from "./mfa";

/**
 * Deployment and instance settings (FR-57, FR-72, SEC-15).
 *
 * The platform catalog is CLOSED. `platform_settings` is a key-value table, so nothing in the
 * schema stops a typo from creating `audit.retention_day` beside `audit.retention_days` and
 * leaving the reader on its default for ever; a closed catalog turns that into a 400 that
 * names the keys that exist. Adding a setting is one entry here plus its reader.
 *
 * A setting that needs a credential declares `secret: true` and is written through
 * `SecretStore` vault-first; its `value` column stays null and no route ever returns or logs
 * the value, only the `{ vaultName, secretName }` pointer (SEC-2).
 */

export const settingKeyParamsSchema = z.object({
  key: z
    .string()
    .min(1)
    .max(200)
    .regex(/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/, "A setting key is dotted lowercase words."),
});
export type SettingKeyParams = z.infer<typeof settingKeyParamsSchema>;

/** A webhook the runner will POST to: https only, and never returned once stored. */
export const webhookUrlSchema = z
  .string()
  .max(2048, "A webhook URL is at most 2048 characters.")
  .refine((raw) => {
    try {
      return new URL(raw).protocol === "https:";
    } catch {
      return false;
    }
  }, "A webhook URL must be an absolute https URL.");

export interface PlatformSettingSpec {
  /** Validates the jsonb `value`; absent for a secret-only setting. */
  value?: z.ZodType;
  /** Validates the secret's plaintext on the way in; absent for a plain setting. */
  secret?: z.ZodType;
  label: string;
  /** What a reader falls back to when the row is absent. Never a secret. */
  fallback?: unknown;
}

export const PLATFORM_SETTINGS = {
  /**
   * FR-14, SEC-4: the re-authentication window for the privileged routes (binding writes,
   * platform grants). Owner decision 2026-10-06: OFF by default (`0`, and a missing row), so
   * sign-in is one click everywhere and MFA is Entra Conditional Access's job; a customer who
   * wants a fresh sign-in before those actions sets 60 to 3600 seconds.
   */
  "auth.reauth_max_age_seconds": {
    value: z
      .number()
      .int()
      .refine((seconds) => seconds === 0 || (seconds >= 60 && seconds <= 3600), {
        message: "0 (off), or 60 to 3600 seconds",
      }),
    label: "Re-authentication window (seconds, 0 is off)",
    fallback: 0,
  },
  /**
   * Admin MFA (design 4.1): who must present a sign-in that proved a second factor. A missing row
   * is `off`; the effective mode is the stricter of this and `AUTH_MFA_FLOOR`. A write other than
   * `off` is refused while no configured issuer declares `mfa: "amr"`.
   */
  "auth.mfa_mode": {
    value: mfaModeSchema,
    label: "Multi-factor sign-in",
    fallback: "off",
  },
  /** Owner answer W3-9: "Do not ask again on this browser for 14 days"; 0 turns remembering off. */
  "auth.mfa_remember_days": {
    value: z.number().int().min(0).max(MAX_MFA_REMEMBER_DAYS),
    label: "Remember a browser after a second factor (days)",
    fallback: DEFAULT_MFA_REMEMBER_DAYS,
  },
  /** Owner answer W3-9: a fresh sign-in at least every 12 hours for admins (NIST 800-63B AAL2). */
  "auth.admin_session_max_hours": {
    value: z.number().int().min(1).max(MAX_ADMIN_SESSION_MAX_HOURS),
    label: "Admin sign-in lasts at most (hours)",
    fallback: DEFAULT_ADMIN_SESSION_MAX_HOURS,
  },
  /** SEC-15: how long hot audit rows are kept before the immutable export takes over. */
  "audit.retention_days": {
    value: z.number().int().min(30).max(3650),
    label: "Audit retention (days)",
    fallback: 365,
  },
  /** FR-71: how stale the due scan may look before `/api/health/scan` reports it. */
  "alerts.scan_stale_seconds": {
    value: z.number().int().min(60).max(86_400),
    label: "Stale-scan threshold (seconds)",
    fallback: 300,
  },
  /** FR-72: the deployment-wide Teams webhook. Stored as a secret reference, never a value. */
  "alerts.teams_webhook": {
    secret: webhookUrlSchema,
    label: "Teams webhook",
  },
  "runs.max_concurrent_per_instance": {
    value: z.number().int().min(1).max(100),
    label: "Concurrent runs per instance",
    fallback: 10,
  },
  /** FR-72: feature flags, as a flat map so a new flag needs no migration. */
  "features.flags": {
    value: z.record(z.string().max(64), z.boolean()),
    label: "Feature flags",
    fallback: {},
  },
} as const satisfies Record<string, PlatformSettingSpec>;

export type PlatformSettingKey = keyof typeof PLATFORM_SETTINGS;

/**
 * #2501 (owner 2026-11-02): flags that no code reads any more, on every deployment. A stored key is
 * ignored: the settings view drops it, so the screen never shows it and the next flags save
 * leaves it out. `transformations` went because Transformations is always on, for everyone, and
 * `audit.integrity` because Audit integrity is always on (its screens show on every deployment).
 * `per_entity_incremental` because per-table incremental ships unflagged on the Tables tab
 * (FR-44), and `lakehouse_sql` because the feature was removed. `usage_report` comes back when the
 * usage page ships (#722).
 */
export const RETIRED_FEATURE_FLAGS: readonly string[] = [
  "lakehouse_sql",
  "per_entity_incremental",
  "usage_report",
  "transformations",
  "audit.integrity",
];

export function isPlatformSettingKey(key: string): key is PlatformSettingKey {
  return Object.prototype.hasOwnProperty.call(PLATFORM_SETTINGS, key);
}

export const secretPointerSchema = z
  .object({ vaultName: z.string(), secretName: z.string() })
  .nullable();
export type SecretPointerView = z.infer<typeof secretPointerSchema>;

export const platformSettingViewSchema = z.object({
  key: z.string(),
  label: z.string(),
  /** Null for a secret-only setting and for a key that has never been written. */
  value: z.unknown().nullable(),
  /** The pointer only: which vault, which secret name. Never a version, never a value. */
  secret: secretPointerSchema,
  isSecret: z.boolean(),
  updatedAt: z.string().nullable(),
  /** #2398: who last changed it, as a label (name, masked address, "Removed user", "System"), never an id. */
  updatedByName: z.string().nullable(),
  /**
   * #2501: `features.flags` only, the flag keys this deployment's policy fixes (`DEPLOYMENT_POLICY`).
   * `value` already carries their fixed values; the screen renders no switch for them. Absent: none.
   */
  fixedByDeployment: z.array(z.string()).optional(),
});
export type PlatformSettingView = z.infer<typeof platformSettingViewSchema>;

/**
 * One of `value` or `secretValue`, matching the key's kind. Unknown keys are refused by the
 * route, so this schema only has to say "exactly one of the two".
 */
export const updateSettingBodySchema = z
  .object({
    value: z.unknown().optional(),
    /** Write-only: the plaintext goes to the vault and the row keeps the reference. */
    secretValue: z.string().max(4096).optional(),
  })
  .superRefine((body, ctx) => {
    const hasValue = Object.prototype.hasOwnProperty.call(body, "value");
    if (hasValue && body.secretValue !== undefined) {
      ctx.addIssue({
        code: "custom",
        path: ["secretValue"],
        message: "Send either a value or a secret value, not both.",
      });
    }
    if (!hasValue && body.secretValue === undefined) {
      ctx.addIssue({
        code: "custom",
        path: ["value"],
        message: "Send a value, or a secret value for a secret setting.",
      });
    }
  });
export type UpdateSettingBody = z.infer<typeof updateSettingBodySchema>;

/** FR-72: the egress allow-list, computed from the connector catalog and never stored. */
export const egressViewSchema = z.object({
  /** The union over active definitions, sorted and de-duplicated. */
  fqdns: z.array(z.string()),
  connectors: z.array(z.object({ name: z.string(), fqdns: z.array(z.string()) })),
  /** Active definitions that declare no hosts, which is "unrestricted" and worth naming. */
  unrestricted: z.array(z.string()),
  /**
   * Hosts the RUNNER posts notifications to (R17), from `NOTIFY_HOSTS` in
   * `shared/types/notifications.ts`, on port 443. Not part of `fqdns`, which stays the connector
   * union. Optional so a server without the notification module renders as before. The purposes
   * are spelled here rather than imported: `notifications.ts` imports this file, and the reverse
   * import would be an ESM cycle.
   */
  platform: z
    .array(
      z.object({
        purpose: z.enum(["email", "teams"]),
        label: z.string(),
        fqdns: z.array(z.string()),
        /** False for email until the runner records a key reference (no row, or not_configured). */
        configured: z.boolean(),
      }),
    )
    .optional(),
});
export type EgressView = z.infer<typeof egressViewSchema>;

/**
 * Per-instance settings (FR-57, FR-72). The Teams webhook is a secret reference on
 * `instance_bindings`; the rest is a jsonb document under the `instance.<id>.settings` key,
 * because no per-instance settings column exists in this release's schema.
 */
export const instanceSettingsBodySchema = z
  .object({
    retentionDays: z.number().int().min(30).max(3650).optional(),
    maxConcurrentRuns: z.number().int().min(1).max(100).optional(),
    notifyOnSuccess: z.boolean().optional(),
    /** Write-only. Null clears the stored reference; the secret itself is left in the vault. */
    teamsWebhookUrl: webhookUrlSchema.nullable().optional(),
  })
  .refine((body) => Object.keys(body).length > 0, "Send at least one setting to change.");
export type InstanceSettingsBody = z.infer<typeof instanceSettingsBodySchema>;

export const instanceSettingsViewSchema = z.object({
  instanceId: z.uuid(),
  retentionDays: z.number(),
  maxConcurrentRuns: z.number(),
  notifyOnSuccess: z.boolean(),
  /**
   * The pointer only (SEC-2); null when no webhook is configured, and null for a caller without
   * `instance.settings.write` (SEC-17, round 3): the vault and secret names are internals a
   * Viewer's browser has no use for.
   */
  teamsWebhook: secretPointerSchema,
  /** Whether a webhook is stored, for every caller: what the screen says instead of the names. */
  teamsWebhookStored: z.boolean(),
  /**
   * #1111: the host the stored webhook posts to (`contoso.webhook.office.com`), recorded at save
   * from the URL the admin typed; never the path, which carries the webhook's signature. Null when
   * no webhook is stored, or for one saved before the host was recorded.
   */
  teamsWebhookHost: z.string().nullable(),
});
export type InstanceSettingsView = z.infer<typeof instanceSettingsViewSchema>;

/** Defaults a reader falls back to when the instance has no settings document yet. */
export const INSTANCE_SETTINGS_DEFAULTS = {
  retentionDays: 365,
  maxConcurrentRuns: 10,
  notifyOnSuccess: false,
} as const;

/**
 * The namespace `platform_settings` keys for one instance's settings document live under.
 *
 * Instance settings share the platform table but are not platform settings: they are one
 * document per instance, keyed by id, and they are read and written only through
 * `/instances/current/settings`. The prefix is what lets the platform catalog tell the two
 * apart (`isInstanceSettingsKey`).
 */
export const INSTANCE_SETTINGS_KEY_PREFIX = "instance.";

/** The `platform_settings` key that holds one instance's settings document. */
export function instanceSettingsKey(instanceId: string): string {
  return `${INSTANCE_SETTINGS_KEY_PREFIX}${instanceId}.settings`;
}

/** True for a key that holds an instance's settings document rather than a platform setting. */
export function isInstanceSettingsKey(key: string): boolean {
  return key.startsWith(INSTANCE_SETTINGS_KEY_PREFIX);
}

/**
 * The namespace `platform_settings` keys for a tenant's own settings live under (T-51, round 8),
 * kept apart from the platform catalog the way instance documents are (`isTenantSettingsKey`).
 * No platform setting starts with it.
 */
export const TENANT_SETTINGS_KEY_PREFIX = "tenant.";

/** The `platform_settings` key that holds one tenant's connector choice. */
export function tenantConnectorAccessKey(tenantId: string): string {
  return `${TENANT_SETTINGS_KEY_PREFIX}${tenantId}.connector_access`;
}

/** True for a key that holds a tenant's setting rather than a platform setting. */
export function isTenantSettingsKey(key: string): boolean {
  return key.startsWith(TENANT_SETTINGS_KEY_PREFIX);
}

/**
 * The namespace for one connector's platform availability (T-51): kept out of
 * `connector_definitions` so a re-seed never resets it, and out of the platform catalog the same
 * way. No platform setting starts with it.
 */
export const CONNECTOR_SETTINGS_KEY_PREFIX = "connector.";

/** The `platform_settings` key that says whether a connector is available for new connections. */
export function connectorAvailabilityKey(connectorName: string): string {
  return `${CONNECTOR_SETTINGS_KEY_PREFIX}${connectorName}.availability`;
}

/** True for a key that holds a connector's availability rather than a platform setting. */
export function isConnectorSettingsKey(key: string): boolean {
  return key.startsWith(CONNECTOR_SETTINGS_KEY_PREFIX);
}
